import { z } from "zod";
import {
	type FormRule,
	FormRulesEnvelopeSchema,
} from "@/features/form-editor/types/form-rule-types";
import {
	type LayoutElement,
	layoutElementSchema,
	parseFormLayoutUnknown,
} from "@/lib/layoutRenderer/schemas/layout.schema";
import { resolveLayoutFromParsedLayoutJson } from "./form-definition-layout-parse.util";

/**
 * Parsed layout envelope shape — strict, without `formRules`.
 * Persistence wire JSON may still include `formRules` until the API supports a root field.
 */
export const LayoutJsonEnvelopeSchema = z
	.object({
		title: z.string().default(""),
		description: z.string().default(""),
		layout: layoutElementSchema,
	})
	.strict();

export type LayoutJsonEnvelope = z.infer<typeof LayoutJsonEnvelopeSchema> & {
	/** Strict parse strips legacy `formRules`; they never appear on the output envelope. */
	formRules?: undefined;
};

/** Legacy rows may still have `formRules` inside the blob. */
export const LegacyLayoutJsonEnvelopeSchema = LayoutJsonEnvelopeSchema.extend({
	formRules: z.array(z.unknown()).optional(),
});

export interface ParseLayoutJsonWireResult {
	envelope: LayoutJsonEnvelope;
	legacyFormRules?: unknown[];
}

/**
 * JSON.parse wire `layoutJson` and return raw `envelope.formRules` when present.
 * Does not require layout Zod validation — used for legacy rule hydration fallback.
 */
export function extractRawFormRulesFromLayoutJsonWire(
	raw: string | null | undefined,
): unknown[] | undefined {
	if (raw == null || String(raw).trim() === "") return undefined;
	try {
		const parsed: unknown = JSON.parse(String(raw));
		if (
			parsed === null ||
			typeof parsed !== "object" ||
			Array.isArray(parsed)
		) {
			return undefined;
		}
		const formRules = (parsed as Record<string, unknown>).formRules;
		if (!Array.isArray(formRules)) return undefined;
		return formRules;
	} catch {
		return undefined;
	}
}

function normalizeParsedLayoutJsonRoot(
	parsed: Record<string, unknown>,
): Record<string, unknown> {
	const layoutCandidate = resolveLayoutFromParsedLayoutJson(parsed);
	if (layoutCandidate === null || typeof layoutCandidate !== "object") {
		return parsed;
	}
	const validated = parseFormLayoutUnknown(layoutCandidate);
	if (!validated.ok) {
		return parsed;
	}
	if (
		"layout" in parsed &&
		parsed.layout !== null &&
		parsed.layout !== undefined
	) {
		return parsed;
	}
	return {
		title: typeof parsed.title === "string" ? parsed.title : "",
		description:
			typeof parsed.description === "string" ? parsed.description : "",
		layout: validated.layout,
	};
}

/**
 * Parses a wire `layoutJson` string into a typed envelope.
 * Tolerates legacy blobs that include `formRules` (stripped from the returned envelope).
 */
export function parseLayoutJsonWire(
	raw: string | null | undefined,
): ParseLayoutJsonWireResult | null {
	if (raw == null || String(raw).trim() === "") return null;
	try {
		const parsed: unknown = JSON.parse(String(raw));
		if (
			parsed === null ||
			typeof parsed !== "object" ||
			Array.isArray(parsed)
		) {
			return null;
		}
		const normalized = normalizeParsedLayoutJsonRoot(
			parsed as Record<string, unknown>,
		);
		const legacyFormRules = extractRawFormRulesFromLayoutJsonWire(raw);
		const layoutCandidate = resolveLayoutFromParsedLayoutJson(normalized);
		if (layoutCandidate === null || typeof layoutCandidate !== "object") {
			return null;
		}
		const validated = parseFormLayoutUnknown(layoutCandidate);
		if (!validated.ok) return null;

		const envelope = LayoutJsonEnvelopeSchema.parse({
			title: typeof normalized.title === "string" ? normalized.title : "",
			description:
				typeof normalized.description === "string"
					? normalized.description
					: "",
			layout: validated.layout,
		});

		return legacyFormRules !== undefined
			? { envelope, legacyFormRules }
			: { envelope };
	} catch {
		return null;
	}
}

/**
 * Validates and serializes a layout envelope for API persistence.
 * Rejects unknown keys (e.g. leaked `formRules`) via `.strict()`.
 */
export function serializeLayoutJsonWire(envelope: LayoutJsonEnvelope): string {
	const validated = LayoutJsonEnvelopeSchema.parse(envelope);
	return JSON.stringify(validated);
}

/**
 * Serializes a layout envelope with embedded form rules for API persistence.
 * The backend persists rules inside the `layoutJson` wire string, not at the root.
 */
export function serializeLayoutJsonWireForPersistence(
	envelope: LayoutJsonEnvelope,
	formRules: FormRule[],
): string {
	const validatedEnvelope = LayoutJsonEnvelopeSchema.parse(envelope);
	const validatedRules = FormRulesEnvelopeSchema.parse(formRules);
	const wire = LegacyLayoutJsonEnvelopeSchema.parse({
		...validatedEnvelope,
		formRules: validatedRules,
	});
	return JSON.stringify(wire);
}

/**
 * Builds a strict layout envelope from a layout tree (layout-only legacy wire JSON).
 */
export function layoutJsonEnvelopeFromLayout(
	layout: LayoutElement,
	title = "",
	description = "",
): LayoutJsonEnvelope {
	return LayoutJsonEnvelopeSchema.parse({ title, description, layout });
}
